export const limit = 10;

export const dateFormat = 'MMM D, YYYY';
export const dateFormatWithMonth = 'MM-DD-YYYY';
export const completeDateFormat = 'MM-DD-YYYYTHH:mm:ss.SSSZ';
export const dateFormatWithTime = 'MM-DD-YYYY hh:mm A';
export const dateYearFormatWithTime = 'MM-DD-YYYY hh:mm:ss';
export const dateFormatWithDate = 'DD MMMM';
export const dateFormatFullMonth = 'D MMMM YYYY';
export const DATE_FORMAT_MMM_DD_YYYY = 'MMM DD, YYYY';
export const dateTimeFormatWithSlash = 'MM/DD/YY hh:mm A';
export const dateTimeYearFormat = 'MM-DD-YYYY hh:mmA';
export const monthDateYear = 'MMMM D, YYYY';
export const dateFormatStartWithYear = 'YYYY-MM-DD';
export const zipCodeRegex = /^\/|\/$/g;

/**
 * Get gender options for dropdown with localization support
 * @param t - Translation function from react-i18next
 * @returns Array of options with label and value
 */

export const getGenderOptions = (t: any) => [
  {
    label: t('peopleDetail.chooseGender'),
    value: ''
  },
  {
    label: t('peopleDetail.male'),
    value: 'male'
  },
  {
    label: t('peopleDetail.female'),
    value: 'female'
  }
];

export const getFileType = (
  url: string
): 'image' | 'video' | 'pdf' | 'unknown' => {
  if (!url) return 'unknown';
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i)) {
    return 'image';
  }
  if (lowerUrl.match(/\.(mp4|mov|avi|wmv|flv|webm|mkv)(\?|$)/i)) {
    return 'video';
  }
  if (lowerUrl.match(/\.pdf(\?|$)/i)) {
    return 'pdf';
  }
  return 'unknown';
};

// =============================================================================
// RELATIONSHIP TYPES - Production Ready
// =============================================================================

/**
 * Blood Relationship Types (Biological/Genetic connections)
 * These relationships form the main tree structure
 */
export const BLOOD_RELATIONSHIP_TYPES = [
  'FATHER',
  'MOTHER',
  'PARENT',
  'SON',
  'DAUGHTER',
  'CHILD',
  'SIBLINGS',
  'HALF_SIBLING',
  'SAME_SEX_PARENTS'
] as const;

/**
 * Non-Blood Relationship Types (Legal/Social connections)
 * These relationships are shown in subtree view (apple icon)
 */
export const NON_BLOOD_RELATIONSHIP_TYPES = [
  'SPOUSE',
  'DIVORCED_SPOUSE',
  'STEP_FATHER',
  'STEP_MOTHER',
  'STEP_PARENT',
  'STEP_CHILD',
  'STEP_SIBLING',
  'ADOPTIVE_PARENT',
  'ADOPTIVE_CHILD',
  'FOSTER_PARENT',
  'FOSTER_CHILD'
] as const;

/**
 * All valid relationship types combined
 */
export const ALL_RELATIONSHIP_TYPES = [
  ...BLOOD_RELATIONSHIP_TYPES,
  ...NON_BLOOD_RELATIONSHIP_TYPES
] as const;

export type BloodRelationshipType = (typeof BLOOD_RELATIONSHIP_TYPES)[number];
export type NonBloodRelationshipType =
  (typeof NON_BLOOD_RELATIONSHIP_TYPES)[number];
export type RelationshipType = (typeof ALL_RELATIONSHIP_TYPES)[number];

/**
 * Relationship options for dropdown menus
 * Organized by category for better UX
 */
export const relationshipOptions = [
  // Placeholder
  { label: 'Select Relationship', value: '', category: 'default' },

  // ─────────────────────────────────────────────────────────────────────────────
  // PARENT RELATIONSHIPS (Direction: Parent → Child)
  // ─────────────────────────────────────────────────────────────────────────────
  {
    label: 'Father',
    value: 'FATHER',
    category: 'parent',
    direction: 'parent-to-child'
  },
  {
    label: 'Mother',
    value: 'MOTHER',
    category: 'parent',
    direction: 'parent-to-child'
  },
  {
    label: 'Parent',
    value: 'PARENT',
    category: 'parent',
    direction: 'parent-to-child'
  },
  {
    label: 'Same-Sex Parent',
    value: 'SAME_SEX_PARENTS',
    category: 'parent',
    direction: 'parent-to-child'
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP-PARENT RELATIONSHIPS (Non-blood, Direction: Step-Parent → Child)
  // ─────────────────────────────────────────────────────────────────────────────
  {
    label: 'Step Father',
    value: 'STEP_FATHER',
    category: 'step-parent',
    direction: 'parent-to-child'
  },
  {
    label: 'Step Mother',
    value: 'STEP_MOTHER',
    category: 'step-parent',
    direction: 'parent-to-child'
  },
  {
    label: 'Step Parent',
    value: 'STEP_PARENT',
    category: 'step-parent',
    direction: 'parent-to-child'
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // ADOPTIVE/FOSTER RELATIONSHIPS (Non-blood, Direction: Parent → Child)
  // ─────────────────────────────────────────────────────────────────────────────
  {
    label: 'Adoptive Parent',
    value: 'ADOPTIVE_PARENT',
    category: 'adoptive',
    direction: 'parent-to-child'
  },
  {
    label: 'Foster Parent',
    value: 'FOSTER_PARENT',
    category: 'foster',
    direction: 'parent-to-child'
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // CHILD RELATIONSHIPS (Direction: Child → Parent, inverted for layout)
  // ─────────────────────────────────────────────────────────────────────────────
  {
    label: 'Son',
    value: 'SON',
    category: 'child',
    direction: 'child-to-parent'
  },
  {
    label: 'Daughter',
    value: 'DAUGHTER',
    category: 'child',
    direction: 'child-to-parent'
  },
  {
    label: 'Child',
    value: 'CHILD',
    category: 'child',
    direction: 'child-to-parent'
  },
  {
    label: 'Step Child',
    value: 'STEP_CHILD',
    category: 'step-child',
    direction: 'child-to-parent'
  },
  {
    label: 'Adoptive Child',
    value: 'ADOPTIVE_CHILD',
    category: 'adoptive',
    direction: 'child-to-parent'
  },
  {
    label: 'Foster Child',
    value: 'FOSTER_CHILD',
    category: 'foster',
    direction: 'child-to-parent'
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // SIBLING RELATIONSHIPS (Direction: Bidirectional)
  // ─────────────────────────────────────────────────────────────────────────────
  {
    label: 'Sibling',
    value: 'SIBLINGS',
    category: 'sibling',
    direction: 'bidirectional'
  },
  {
    label: 'Half Sibling',
    value: 'HALF_SIBLING',
    category: 'sibling',
    direction: 'bidirectional'
  },
  {
    label: 'Step Sibling',
    value: 'STEP_SIBLING',
    category: 'sibling',
    direction: 'bidirectional'
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // SPOUSE RELATIONSHIPS (Direction: Bidirectional)
  // ─────────────────────────────────────────────────────────────────────────────
  {
    label: 'Spouse',
    value: 'SPOUSE',
    category: 'spouse',
    direction: 'bidirectional'
  },
  {
    label: 'Divorced Spouse',
    value: 'DIVORCED_SPOUSE',
    category: 'spouse',
    direction: 'bidirectional'
  }
] as const;

/**
 * Helper to check if a relationship type is blood-related
 */
export const isBloodRelationship = (type: string): boolean => {
  return BLOOD_RELATIONSHIP_TYPES.includes(
    type.toUpperCase() as BloodRelationshipType
  );
};

/**
 * Helper to check if a relationship type is non-blood
 */
export const isNonBloodRelationship = (type: string): boolean => {
  return NON_BLOOD_RELATIONSHIP_TYPES.includes(
    type.toUpperCase() as NonBloodRelationshipType
  );
};

/**
 * Get the inverse relationship type
 * Example: FATHER → CHILD, SPOUSE → SPOUSE
 */
export const getInverseRelationship = (type: string): string => {
  const inverseMap: Record<string, string> = {
    // Parent ↔ Child
    FATHER: 'CHILD',
    MOTHER: 'CHILD',
    PARENT: 'CHILD',
    SAME_SEX_PARENTS: 'CHILD',
    SON: 'PARENT',
    DAUGHTER: 'PARENT',
    CHILD: 'PARENT',

    // Step Relations
    STEP_FATHER: 'STEP_CHILD',
    STEP_MOTHER: 'STEP_CHILD',
    STEP_PARENT: 'STEP_CHILD',
    STEP_CHILD: 'STEP_PARENT',

    // Adoptive/Foster
    ADOPTIVE_PARENT: 'ADOPTIVE_CHILD',
    ADOPTIVE_CHILD: 'ADOPTIVE_PARENT',
    FOSTER_PARENT: 'FOSTER_CHILD',
    FOSTER_CHILD: 'FOSTER_PARENT',

    // Symmetrical
    SIBLINGS: 'SIBLINGS',
    HALF_SIBLING: 'HALF_SIBLING',
    STEP_SIBLING: 'STEP_SIBLING',
    SPOUSE: 'SPOUSE',
    DIVORCED_SPOUSE: 'DIVORCED_SPOUSE'
  };

  return inverseMap[type.toUpperCase()] || type;
};

/**
 * Constants for Family Tree component
 * Centralized configuration for easy maintenance and consistency
 */

// Node dimensions
export const NODE_WIDTH = 152;
export const NODE_HEIGHT = 200;
export const NODE_OFFSET_X = 76; // Half of width for centering
export const NODE_OFFSET_Y = 100; // Half of height for centering

// Avatar dimensions
export const AVATAR_SIZE = 80;
export const AVATAR_SIZE_SMALL = 48;

// Cytoscape layout configuration (with node dimensions for family tree)
export const LAYOUT_CONFIG = {
  name: 'dagre',
  rankDir: 'TB', // Top to bottom
  nodeSep: 80,
  rankSep: 120,
  animate: true,
  animationDuration: 500,
  fit: true,
  padding: 50,
  // Node dimensions (for family tree layout)
  NODE_WIDTH: NODE_WIDTH,
  NODE_HEIGHT: NODE_HEIGHT,
  PLACEHOLDER_WIDTH: 140,
  PLACEHOLDER_HEIGHT: 70,
  // Spacing
  SIBLING_GAP: 40,
  COUSIN_GAP: 100,
  SPOUSE_GAP: 100,
  GENERATION_GAP: 120,
  NODE_GAP: 100,
  EDGE_STROKE_WIDTH: 2,
  EDGE_STROKE_COLOR: '#000000',
  EDGE_STROKE_COLOR_STEP: '#4A5DEF',
  EDGE_STROKE_COLOR_HALF: '#800080',
  EDGE_STROKE_COLOR_SPOUSE: '#F3A126',
  EDGE_STROKE_COLOR_DIVORCED: '#ef4444',
  EDGE_STROKE_COLOR_ADOPTED: '#1D9C41',
  EDGE_SPOUSE_OFFSET: 10,
  GROUP_LABEL_WIDTH: 210,
  GROUP_LABEL_HEIGHT: 28,
  // Padding
  TOP_PADDING: 100,
  LEFT_PADDING: 50
} as const;

// Cytoscape zoom configuration
export const ZOOM_CONFIG = {
  min: 0.1,
  max: 10,
  step: 1.2,
  wheelSensitivity: 0.15
} as const;

// Avatar color palette
export const AVATAR_COLORS = [
  '#C7B3E5', // Purple
  '#A8C5E2', // Blue
  '#FFB4C7', // Pink
  '#B5DDA4', // Green
  '#FAD4A0', // Orange
  '#D4A5C0' // Mauve
] as const;

// Edge colors by relationship type
export const EDGE_COLORS = {
  spouse: '#F59E0B', // Amber
  parent: '#6EE7B7', // Green
  child: '#FBBF24', // Yellow
  sibling: '#D1D5DB', // Gray
  grandparent: '#D1D5DB', // Gray
  default: '#D1D5DB' // Gray
} as const;

// Edge styles
export const EDGE_STYLES = {
  width: {
    default: 2,
    spouse: 3,
    selected: 4
  },
  style: {
    solid: 'solid',
    dashed: 'dashed'
  },
  // Relationship-specific edge styles
  SOLID_BLACK: {
    color: '#000000',
    style: 'solid' as const
  },
  SPOUSE: {
    color: '#000000',
    style: 'solid' as const
  },
  DIVORCED: {
    color: '#EF4444', // Red
    style: 'solid' as const
  },
  STEP: {
    color: '#3B82F6', // Blue
    style: 'dotted' as const
  },
  ADOPTED: {
    color: '#22C55E', // Green
    style: 'dashed' as const
  },
  FOSTER: {
    color: '#06B6D4', // Cyan
    style: 'dashed' as const
  },
  HALF_SIBLING: {
    color: '#8B5CF6', // Purple
    style: 'dashed' as const
  },
  PLACEHOLDER: {
    color: '#9CA3AF', // Gray
    style: 'dashed' as const
  }
} as const;

// Animation durations (milliseconds)
export const ANIMATION_DURATION = {
  layout: 500,
  cardCreation: 100,
  cardUpdate: 550,
  modal: 300
} as const;

// Debounce/Throttle delays (milliseconds)
export const PERFORMANCE_CONFIG = {
  debounceDelay: 300,
  throttleDelay: 100,
  cardUpdateDelay: 16 // ~60fps
} as const;

// Modal z-index layers
export const Z_INDEX = {
  cytoscape: 1,
  nodeCards: 10,
  contextMenu: 50,
  connectionLine: 1000,
  highlightedCard: 1001,
  modal: 9999
} as const;

// Drag detection threshold (pixels)
export const DRAG_THRESHOLD = 2;

// Relationship type options
export const RELATIONSHIP_TYPES = [
  'parent',
  'child',
  'spouse',
  'sibling',
  'grandparent',
  'grandchild',
  'other'
] as const;

// Status options
export const STATUS_OPTIONS = ['Living', 'Deceased'] as const;

// Gender options
export const GENDER_OPTIONS = ['Male', 'Female'] as const;

// CSS class names
export const CSS_CLASSES = {
  nodeCard: 'node-card-overlay',
  cardsContainer: 'node-cards-container',
  menuButton: 'menu-btn-wrapper',
  connectButton: 'connect-btn-wrapper',
  connectionSource: 'connection-source',
  connectionTarget: 'connection-target-hover'
} as const;

// Event names
export const CYTOSCAPE_EVENTS = {
  tap: 'tap',
  grab: 'grab',
  drag: 'drag',
  free: 'free',
  zoom: 'zoom',
  pan: 'pan',
  viewport: 'viewport',
  position: 'position',
  layoutStop: 'layoutstop'
} as const;

// Error messages
export const ERROR_MESSAGES = {
  cytoscapeInit: 'Failed to initialize Cytoscape',
  invalidNode: 'Invalid node data provided',
  invalidEdge: 'Invalid edge data provided',
  duplicateConnection: 'Connection already exists',
  selfConnection: 'Cannot connect node to itself',
  missingCallback: 'Required callback function is missing'
} as const;

// Validation rules
export const VALIDATION_RULES = {
  name: {
    minLength: 2,
    maxLength: 50
  },
  date: {
    minYear: 1800,
    maxYear: new Date().getFullYear() + 100
  }
} as const;

// Performance optimization flags
export const OPTIMIZATION_FLAGS = {
  textureOnViewport: true,
  hideEdgesOnViewport: true,
  hideLabelsOnViewport: false,
  motionBlur: false,
  pixelRatio: 'auto' as const
} as const;
export const DATE_FORMAT_MMM_DD_YYYY_HH_MM = 'MMM DD, YYYY h:mm A';

export const hasHtmlContent = (html?: string) => {
  if (!html) return false;

  const text = html
    .replace(/<[^>]*>/g, '') // remove tags
    .replace(/&nbsp;/g, '') // remove editor spaces
    .trim();

  return text.length > 0;
};

export const formatBytes = (bytes: number = 0) => {
  if (!bytes) return '0 MB';

  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb.toFixed(2)} GB`;

  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
};

// Memory slideshow timing. A slide occupies the autoplay delay plus Swiper's
// slide transition, so the audio crop must be sized against that total rather
// than the delay alone - otherwise the track runs out before the last slides.
export const SLIDE_DELAY_MS = 3700;
export const SLIDE_TRANSITION_MS = 300;
export const SLIDE_TOTAL_SECONDS =
  (SLIDE_DELAY_MS + SLIDE_TRANSITION_MS) / 1000;

// Autoplay timers can fire late but never early, so a long slideshow drifts
// past its nominal length and always in the same direction. Crop a little more
// audio than the slides strictly need: the player stops the track on the last
// slide, so the surplus is never heard, while a shortfall is audible as silence.
export const SLIDE_AUDIO_TAIL_BUFFER_SECONDS = 2;

/**
 * Audio length to select for a slideshow of `numberOfPhotos` slides, including
 * the drift buffer. Callers cap this at the original track's duration.
 */
export const getSlideshowAudioSeconds = (numberOfPhotos: number) =>
  numberOfPhotos * SLIDE_TOTAL_SECONDS + SLIDE_AUDIO_TAIL_BUFFER_SECONDS;
